// ===============================================
// ToolsGuidePanel.jsx
// Sliding Tools Guide Panel positioned on the left
// Handles visibility and toggle functionality with enhanced visibility
// ===============================================

import React, { forwardRef } from 'react';
// Added icons for Image, Brain (for memory), Mail, Search, Database, Cloud, Rewind, Trash2, Maximize2, Scissors, Bookmark, NotebookPen, MessageSquarePlus, Calendar, Pin, X
import { X, Rewind, Trash2, Maximize2, Scissors, Bookmark, NotebookPen, MessageSquarePlus, Calendar, Brain, Pin, Search, Database, Cloud, Image, Mail } from 'lucide-react';
import PropTypes from 'prop-types';
// Using the dedicated theme hook instead of general settings
import { useTheme } from '@/managers/ThemeProvider';

/* =============================================== */
/*              FeatureItem Component              */
/* Renders an individual feature with icon, title, and description */
/* =============================================== */
const FeatureItem = ({ icon, title, description }) => {
  // Accessing theme state directly from the ThemeProvider
  const { isLightTheme } = useTheme();
  return (
    <div className="mb-4">
      <div className="flex items-center mb-1">
        <span className={`text-[11px] font-medium ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>{title}</span>
        <div className="ml-2">{icon}</div>
      </div>
      <p className={`text-[10px] leading-relaxed pl-0 ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
        {description}
      </p>
    </div>
  );
};

FeatureItem.propTypes = {
  icon: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

/* =============================================== */
/*              ToolsGuidePanel Component          */
/* Sliding panel that provides a quick tools guide */
/* Positioned 60px from the top on the left of the viewport */
/* =============================================== */
const ToolsGuidePanel = forwardRef(({ isOpen, toggleOpen }, ref) => {
  // Accessing theme state directly from the ThemeProvider
  const { isLightTheme } = useTheme();

  return (
    <div
      className={`
        fixed left-0 top-[60px] pointer-events-auto
        transition-transform duration-300 ease-in-out z-30
        ${isOpen ? 'translate-x-0' : 'translate-x-[-100%]'}
      `}
      ref={ref}
    >
      <div className={`w-[280px] rounded-r-lg flex flex-col
                ${isLightTheme ? 'border border-border-color bg-white' : 'border border-white/5 bg-surface backdrop-blur-sm'}`}>
        <div className={`p-3 border-b relative
          ${isLightTheme ? 'border-border-color/20' : 'border-white/5'}`}>
          <div className={`text-[10px] font-medium tracking-wider text-center
                    ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
            QUICK TOOLS GUIDE
          </div>
          <button
            onClick={toggleOpen}
            className="absolute right-2 top-2 p-1 rounded-md hover:bg-surface-dark transition-colors duration-200"
            aria-label="Close Tools Guide"
          >
            <X size={16} className="text-on-surface" />
          </button>
        </div>

        <div className="p-3 space-y-3 overflow-y-auto hide-scrollbar max-h-[calc(100vh-240px)]">

           {/* =============================================== */}
           {/*           WHAT'S NEW IN SHELBULA V4             */}
           {/* =============================================== */}
           <div className={`pb-3 ${isLightTheme ? 'border-b border-border-color/20' : 'border-b border-white/10'}`}>
             <h5 className={`text-[10px] font-bold mb-3 ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
               WHAT'S NEW IN SHELBULA V4
             </h5>
             {/* Built-in Tools */}
             <FeatureItem
               icon={<Search size={14} className="text-primary" />}
               title="New Built-in Tools"
               description="Access powerful tools like Google Search, Website Research, Scheduled Assistant Tasks, and Email Sending directly within the chat."
             />
              {/* Automatic Memory (Pro) */}
              <FeatureItem
               icon={<Brain size={14} className="text-primary" />}
               title="Automatic Memory (Pro)"
               description="Your bot remembers your preferences and styles across all conversations for a more personalized experience."
             />
              {/* On-Demand Knowledge Bank (Pro) */}
             <FeatureItem
               icon={<Database size={14} className="text-primary" />}
               title="On-Demand Knowledge Bank (Pro)"
               description="Store knowledge as needed and retrieve it on demand for domain-specific expertise and advanced memory."
             />
              {/* MCP Support (Pro) */}
             <FeatureItem
               icon={<Cloud size={14} className="text-primary" />}
               title="MCP Support (Pro)"
               description="Shelbula acts as a universal MCP client, enabling Model Context Protocol capabilities with any platform or model, even those without native support."
             />
           </div>


           {/* =============================================== */}
           {/*            FAMILIAR TOOLS FROM V3               */}
           {/* =============================================== */}
           <div className={`pt-3 pb-3 ${isLightTheme ? 'border-b border-border-color/20' : 'border-b border-white/10'}`}>
             <h5 className={`text-[10px] font-bold mb-3 ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
               FAMILIAR TOOLS FROM V3
             </h5>
             {/* Message Rewind & Selective Delete */}
             <FeatureItem
               icon={<Rewind size={14} className="text-primary" />}
               title="Message Rewind & Selective Delete"
               description="Rewind to any message or remove individual messages to manage conversation context."
             />
             {/* Switch Bots Mid-Conversation */}
             <FeatureItem
               icon={<MessageSquarePlus size={14} className="text-primary" />} // Reusing icon, maybe find a better one?
               title="Switch Bots Mid-Conversation"
               description="Transfer conversations between different bots seamlessly."
             />
             {/* Custom Context Length */}
             <FeatureItem
               icon={<Scissors size={14} className="text-primary" />}
               title="Custom Context Length"
               description="Control chat memory depth per conversation."
             />
             {/* Quick Chat Summaries */}
             <FeatureItem
               icon={<NotebookPen size={14} className="text-primary" />}
               title="Quick Chat Summaries"
               description="Get instant summaries of any conversation."
             />
             {/* Quick Snippets & Notes */}
             <FeatureItem
               icon={<Bookmark size={14} className="text-primary" />}
               title="Quick Snippets & Notes"
               description="Save code blocks and messages to your snippets panel."
             />
           </div>

           {/* =============================================== */}
           {/*        DEVELOPER-FRIENDLY CODE FEATURES         */}
           {/* =============================================== */}
           <div className={`pt-3 pb-3 ${isLightTheme ? 'border-b border-border-color/20' : 'border-b border-white/10'}`}>
             <h5 className={`text-[10px] font-bold mb-3 ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
               DEVELOPER-FRIENDLY CODE FEATURES
             </h5>
             <FeatureItem
               icon={<Maximize2 size={14} className="text-primary" />}
               title="Developer-Friendly Code Features"
               description="Beautiful syntax, instant copy, comment stripping, snippet saving, and direct downloads for robust iteration."
             />
           </div>

           {/* =============================================== */}
           {/*                 COMING SOON                     */}
           {/* =============================================== */}
           <div className={`pt-3 ${isLightTheme ? 'border-b border-border-color/20' : 'border-b border-white/10'}`}>
              <h5 className={`text-[10px] font-bold mb-3 ${isLightTheme ? 'text-on-surface' : 'text-white'}`}>
               COMING SOON
             </h5>
             <FeatureItem
               icon={<Calendar size={14} className="text-primary" />} // Using Calendar icon for Coming Soon
               title="Workspace Agent Modes"
               description="Persistent workspace-native agent modes are still evolving. Expect deeper project isolation, better code operations, and stronger long-running workspace execution as this surface matures."
               />
           </div>

        </div>

        {/* Bottom Close Button */}
        <div className={`p-2 border-t flex justify-end ${isLightTheme ? 'border-border-color/20' : 'border-white/5'}`}>
          <button
            onClick={toggleOpen}
            className="p-1 rounded-md hover:bg-surface-dark transition-colors duration-200"
            aria-label="Close Tools Guide"
          >
            <X size={16} className="text-on-surface" />
          </button>
        </div>
      </div>
    </div>
  );
});

ToolsGuidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired
};

export default ToolsGuidePanel;